import { autoPlacement, autoUpdate, flip, FloatingPortal, shift, useFloating } from '@floating-ui/react';
import { type CalendarDate, createCalendar, parseDate } from '@internationalized/date';
import { useCalendarState } from '@react-stately/calendar';
import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { I18nProvider, useCalendar } from 'react-aria';
import { useIntl } from 'react-intl';

import { IntlProvider, type SupportedLocale } from '../../../contexts/IntlProvider';
import cs from '../../../locales/cs.json';
import de from '../../../locales/de.json';
import en from '../../../locales/en.json';
import es from '../../../locales/es.json';
import fr from '../../../locales/fr.json';
import it from '../../../locales/it.json';
import pt from '../../../locales/pt.json';
import zh from '../../../locales/zh.json';
import { isDefined } from '../../../utils';
import { SgComponentIcon } from '../../display/ComponentIcon';
import { SgTooltip } from '../../feedback/SgTooltip';
import { ClearButton } from './ClearButton';
import { SgDatePickerProvider } from './contexts/SgDatePickerProvider';

import './index.css';
import { SgDatePickerMenu } from './Dropdown/DropdownMenu';
import { Input } from './Input/Input';
import { useFocusManagement } from './Input/useFocusManagement';
import { useValidationManager } from './Input/useValidationManager';
import type { SgDatePickerProps } from './types';
import { createIsDateUnavailable, formatValueForInput, getDefaultDateFormat } from './utils';

const LOCALES = {
  en,
  fr,
  de,
  es,
  it,
  pt,
  cs,
  zh,
};

// This component is too complex and triggers too many rerenders.
// It is deliberately not refactored here: it will be replaced by a new version of the date picker
// provided by sgme-ui. The known functional issues are listed in the markdown notes of this folder.
export function SgDatePicker(props: SgDatePickerProps): React.ReactElement {
  const intlLocale = props.locale?.split('-')[0] || 'en';

  return (
    <IntlProvider locales={LOCALES} defaultLocale={intlLocale as SupportedLocale}>
      <SgDatePickerInternal {...props} />
    </IntlProvider>
  );
}

function SgDatePickerInternal(props: SgDatePickerProps): React.ReactElement {
  const {
    locale = 'en-GB',
    dateInputCultureInfo = 'en-GB',
    dateFormat,
    inputId,
    defaultValue,
    value,
    onChange,
    itemSelection,
    placeholder,
    showDropdownIndicator = true,
    dropdownParent,
    dropdownPlacement = 'bottom',
    dropdownAlignment = 'start',
    className,
    size = 'md',
    purpose = 'default',
    isOpen: controlledIsOpen,
    disabled,
    readonly,
    yearControls,
    showClearButton = true,
    closeWhenClickOutside = true,
    validationState,
    validationMessage,
    validationDisplay = 'aside',
    validationTooltipPosition = 'top',
    disablePresets,
    customPresets,
    presetsDisplayGrid = 1,
    onIsOpenChange,
    onCustomPresetClick,
    onInputCleared,
    onInputChanged,
    onFocus,
    onBlurInput,
    inputRef,
    dropdownRef,
    color,
  } = props;

  const { formatMessage } = useIntl();

  const effectiveDateFormat = dateFormat || getDefaultDateFormat(locale);
  const effectivePlaceholder = placeholder || effectiveDateFormat;

  const isControlled = Object.keys(props).includes('value');

  const isDateUnavailable = useMemo(() => createIsDateUnavailable(disablePresets), [disablePresets]);

  const validation = useValidationManager({
    externalState: validationState,
    externalMessage: validationMessage,
    locale,
    dateInputCultureInfo,
    dateFormat: effectiveDateFormat,
    isDateUnavailable,
    isControlled,
  });

  const [isInternalOpen, setIsInternalOpen] = useState(false);
  const isOpen = isDefined(controlledIsOpen) ? controlledIsOpen : isInternalOpen;

  const [shouldAutoFocusCalendar, setShouldAutoFocusCalendar] = useState(false);
  const [isHover, setIsHover] = useState(false);

  /** Create internal refs if not provided */
  const internalInputRef = useRef<HTMLInputElement | null>(null);
  const internalDropdownRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  /** Use provided refs or fallback to internal ones */
  const actualInputRef = inputRef || internalInputRef;
  const actualDropdownRef = dropdownRef || internalDropdownRef;

  const [internalValue, setInternalValue] = useState<CalendarDate | null>(() => {
    if (isControlled) {
      return null;
    }

    if (!defaultValue) {
      return null;
    }

    try {
      return parseDate(defaultValue);
    } catch {
      return null;
    }
  });

  const parsedValue = useMemo(() => {
    if (!isControlled) {
      return internalValue;
    }

    if (!value) {
      return null;
    }

    try {
      return parseDate(value);
    } catch (error) {
      if (!isControlled) {
        console.warn('Invalid value format:', value, error);
      }

      return null;
    }
  }, [value, isControlled, internalValue]);

  // React ARIA Calendar State
  const calendarState = useCalendarState({
    locale,
    createCalendar,
    isDisabled: disabled,
    isReadOnly: readonly,
    isDateUnavailable: isDateUnavailable as any,
    value: parsedValue,
  });

  const { calendarProps, prevButtonProps, nextButtonProps } = useCalendar(
    {
      isDisabled: disabled,
      isReadOnly: readonly,
      isDateUnavailable: isDateUnavailable as any,
    },
    calendarState,
  );

  // Create custom title using the specified locale instead of browser locale
  const title = useMemo(() => {
    const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: calendarState.timeZone });
    const yearFormatter = new Intl.DateTimeFormat(locale, { year: 'numeric', timeZone: calendarState.timeZone });

    const jsDate = calendarState.focusedDate.toDate(calendarState.timeZone);
    const month = monthFormatter.format(jsDate);
    const year = yearFormatter.format(jsDate);

    return `${month} ${year}`;
  }, [calendarState.focusedDate, calendarState.timeZone, locale]);

  const inputValue = useMemo(() => {
    if (parsedValue) {
      return formatValueForInput(parsedValue, locale, effectiveDateFormat);
    }
    return '';
  }, [parsedValue, locale, effectiveDateFormat]);

  // oxlint-disable-next-line exhaustive-deps -- calendarState changes every time
  useEffect(() => {
    if (parsedValue && !isOpen) {
      calendarState.setFocusedDate(parsedValue);
    }
  }, [parsedValue, isOpen]);

  // Handle open/close
  const handleClose = useCallback(() => {
    setIsInternalOpen(false);
    onIsOpenChange?.(false);
    const inputElement = containerRef.current?.querySelector('input') as HTMLInputElement;
    if (inputElement === document.activeElement) {
      inputElement.blur();
    }
  }, [onIsOpenChange]);

  const handleOpen = useCallback(
    (shouldFocus: boolean) => {
      setIsInternalOpen(true);
      onIsOpenChange?.(true);
      setShouldAutoFocusCalendar(shouldFocus);
    },
    [onIsOpenChange],
  );

  const focusManagement = useFocusManagement({
    inputRef: actualInputRef,
    dropdownRef: actualDropdownRef,
    isOpen,
    shouldAutoFocusCalendar,
  });

  const handleToggle = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }

      if (readonly || disabled) {
        return;
      }

      const newIsOpen = !isOpen;
      setIsInternalOpen(newIsOpen);
      onIsOpenChange?.(newIsOpen);

      if (newIsOpen) {
        setShouldAutoFocusCalendar(true);
      }
    },
    [onIsOpenChange, isOpen, readonly, disabled],
  );

  const handleClear = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (isControlled) {
        onChange?.('');
      } else {
        setInternalValue(null);
        onChange?.('');
      }

      onInputCleared?.();
    },
    [isControlled, onChange, onInputCleared],
  );

  const handleDropdownButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    // On Tab when dropdown is open, focus into the dropdown
    if (e.key === 'Tab' && !e.shiftKey && isOpen) {
      e.preventDefault();
      // Find the first focusable element in the dropdown
      const firstFocusable = actualDropdownRef.current?.querySelector('button:not(:disabled)') as HTMLElement;
      if (firstFocusable) {
        firstFocusable.focus();
      }
    }
  };

  const handleDateChange = useCallback(
    (dateString: string) => {
      if (isControlled) {
        onChange?.(dateString);
      } else {
        if (dateString) {
          try {
            const parsed = parseDate(dateString);
            setInternalValue(parsed);
            onChange?.(dateString);
          } catch (error) {
            console.warn('Failed to parse:', dateString, error);
          }
        } else {
          setInternalValue(null);
          onChange?.('');
        }
      }
    },
    [isControlled, onChange],
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is outside both the container and the dropdown
      if (
        closeWhenClickOutside &&
        containerRef.current &&
        !containerRef.current.contains(target) &&
        actualDropdownRef.current &&
        !actualDropdownRef.current.contains(target)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose, closeWhenClickOutside, actualDropdownRef.current]);

  // Floating UI configuration
  const { refs, floatingStyles, isPositioned } = useFloating({
    open: isOpen,
    strategy: 'fixed',
    onOpenChange: (open) => {
      setIsInternalOpen(open);
      onIsOpenChange?.(open);
    },
    placement:
      dropdownPlacement === 'auto'
        ? undefined
        : dropdownPlacement === 'top'
          ? `top-${dropdownAlignment}`
          : `bottom-${dropdownAlignment}`,
    whileElementsMounted: autoUpdate,
    middleware: [
      dropdownPlacement === 'auto'
        ? autoPlacement({
            allowedPlacements: [`top-${dropdownAlignment}`, `bottom-${dropdownAlignment}`, 'right-start', 'right-end'],
            rootBoundary: 'viewport',
          })
        : flip({
            fallbackPlacements: [
              `${dropdownPlacement}-end`,
              `${dropdownPlacement === 'bottom' ? 'top' : 'bottom'}-${dropdownAlignment}`,
              `${dropdownPlacement === 'bottom' ? 'top' : 'bottom'}-end`,
              'right-start',
              'right-end',
              'left-start',
              'left-end',
            ],
            fallbackStrategy: 'bestFit',
            rootBoundary: 'viewport',
          }),
      shift({ padding: 8, rootBoundary: 'viewport' }),
    ],
  });

  const configValue = useMemo(
    () => ({
      inputRef: actualInputRef,
      locale,
      dateFormat,
      isControlled,
      purpose: 'default' as const,
      size,
      color,
      placeholder: effectivePlaceholder,
      dateInputCultureInfo,
      disabled,
      readonly,
      yearControls,
      inputId,
      disablePresets,
      shouldAutoFocusCalendar,
      customPresets,
      presetsDisplayGrid,
      showClearButton,
      focusManagement,
    }),
    [
      actualInputRef,
      locale,
      dateInputCultureInfo,
      dateFormat,
      isControlled,
      size,
      effectivePlaceholder,
      disabled,
      readonly,
      yearControls,
      inputId,
      disablePresets,
      shouldAutoFocusCalendar,
      customPresets,
      presetsDisplayGrid,
      showClearButton,
      focusManagement,
      color,
    ],
  );

  const calendarValue = useMemo(
    () => ({
      calendarState,
      calendarProps,
      prevButtonProps,
      nextButtonProps,
      title,
      inputValue,
      isDateUnavailable,
    }),
    [calendarState, calendarProps, prevButtonProps, nextButtonProps, title, inputValue, isDateUnavailable],
  );

  const dropdownValue = useMemo(
    () => ({
      isOpen,
      isPositioned,
      floatingStyles,
      dropdownRef: actualDropdownRef,
      refs,
    }),
    [isOpen, isPositioned, floatingStyles, actualDropdownRef, refs],
  );

  const actionsValue = useMemo(
    () => ({
      onClose: handleClose,
      onOpen: handleOpen,
      onClear: handleClear,
      onDateChange: handleDateChange,
      onFocus,
      onBlur: onBlurInput,
      onInputChanged,
      onCustomPresetClick,
    }),
    [handleClose, handleOpen, handleClear, handleDateChange, onFocus, onBlurInput, onInputChanged, onCustomPresetClick],
  );

  return (
    <I18nProvider locale={locale}>
      <SgDatePickerProvider
        config={configValue}
        calendar={calendarValue}
        dropdown={dropdownValue}
        validation={validation}
        actions={actionsValue}
      >
        <div ref={containerRef} className={clsx('position-relative')}>
          <div ref={refs.setReference} className={`flex-column input-group w-100 input-group-${size}`}>
            <div
              onMouseEnter={() => setIsHover(true)}
              onMouseLeave={() => setIsHover(false)}
              style={
                disabled
                  ? {
                      backgroundColor: 'transparent',
                      opacity: 0.4,
                      boxShadow: 'inset 0 -0.0625rem 0 var(--bs-border-color, var(--border-color))',
                    }
                  : {}
              }
              className={clsx(
                `form-control w-100 form-control-${size} pe-0`,
                purpose === 'outline' ? 'form-control-outline' : '',
                disabled && 'disabled',
                readonly && 'readonly',
                isDefined(validation.validationState) && `is-${validation.validationState}`,
                className,
              )}
            >
              <div className="flex-grow-1 d-flex justify-content-start align-items-center">
                <SgComponentIcon
                  className={clsx(
                    'icon me-2 mr-2 d-flex align-items-center flex-shrink-0',
                    isOpen ? 'text-primary' : 'text-secondary',
                  )}
                  size={size}
                  data-e2e={itemSelection === 'multiple' ? 'date_range' : 'today'}
                  icon={itemSelection === 'multiple' ? 'date_range' : 'today'}
                />
                <div className="overflow-hidden d-flex flex-wrap flex-grow-1 justify-content-start align-items-center mt-n1">
                  {validationDisplay === 'tooltip' && validation.validationMessage && validation.validationState ? (
                    <SgTooltip
                      mode="hover"
                      backgroundColor={validation.getValidationColor()}
                      arrow={true}
                      placement={validationTooltipPosition}
                    >
                      <SgTooltip.Trigger asChild>
                        <Input />
                      </SgTooltip.Trigger>
                      <SgTooltip.Content>
                        <div className="p-1">{validation.validationMessage}</div>
                      </SgTooltip.Content>
                    </SgTooltip>
                  ) : (
                    <Input />
                  )}
                </div>
                <ClearButton showClearButton={showClearButton} isHover={isHover} />
                {showDropdownIndicator && (
                  <button
                    type="button"
                    onClick={handleToggle}
                    className={`btn btn-flat btn-icon btn-${size}`}
                    onKeyDown={handleDropdownButtonKeyDown}
                    color="secondary"
                    aria-label={
                      isOpen ? formatMessage({ id: 'sgds.date.close' }) : formatMessage({ id: 'sgds.date.open' })
                    }
                    disabled={disabled || readonly}
                  >
                    <SgComponentIcon
                      icon={isOpen ? 'arrow_drop_up' : 'arrow_drop_down'}
                      size={size}
                      data-e2e={isOpen ? 'arrow_drop_up' : 'arrow_drop_down'}
                    />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Dropdown menu */}
          <FloatingPortal root={dropdownParent}>
            <SgDatePickerMenu />
          </FloatingPortal>

          {/* Validation message */}
          {validation.validationMessage && validation.validationState && validationDisplay === 'aside' && (
            <div
              className={clsx(
                'd-block',
                validation.validationState === 'invalid' && 'invalid-feedback text-invalid',
                validation.validationState === 'valid' && 'valid-feedback text-valid',
                validation.validationState === 'warning' && 'text-warning',
                validation.validationState === 'edited' && 'text-edited',
              )}
            >
              {validation.validationMessage}
            </div>
          )}
        </div>
      </SgDatePickerProvider>
    </I18nProvider>
  );
}
